import { AxiosUtil } from '../utils/axios-util'
import axios, { AxiosResponse } from 'axios'
import EventConstants from '../constants/EventConstants'
import { saveAs } from 'file-saver'
import {
  errorResponseNotification,
  successNotification,
} from './notification-service'
import {
  FileDownload,
  FileMetadata,
  ShareFileRequest,
} from './models/fileService'

const regex = new RegExp('.+(' + EventConstants.FILE_TYPES.join('|') + ')$')

export const bytesToSize = bytes => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return 'n/a'
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString())
  if (i === 0) return bytes + ' ' + sizes[i]
  return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i]
}

export const getFile = (fileId: string): Promise<FileMetadata> => {
  const url = `${EventConstants.EVENT_FILES}/${fileId}`

  return AxiosUtil.get(url)
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
    .then(response => response?.data)
}

export const getFiles = (eventId, vendorId) => {
  let url = EventConstants.EVENT_FILES + '?eventObjectId=' + eventId
  // If external user add the vendor's id
  if (vendorId) {
    url += '&vendorObjectId=' + vendorId
  }
  return AxiosUtil.get(url).catch(err => {
    errorResponseNotification(err)
    throw err
  })
}

export interface FileInfo {
  fileName: string
  fileSize: string
  progress: number
  progressMessage: string
  status: string
  isCancellable: boolean
  cancel: () => void
}

export interface UploadFileProps {
  eventObjectId: string
  uploaderObjectId: 'TARGET' | string
  statusCallback: (file: FileInfo, status: string) => void
  file: File
  uploads: { fileName: string; status: string }[]
  eventName: string | null
  bpId: number | null
  bpName: string | null
  postFeedback?: boolean
}

export const uploadFile = ({
  eventObjectId,
  uploaderObjectId,
  statusCallback,
  file,
  uploads,
  eventName,
  bpId,
  bpName,
  postFeedback,
}: UploadFileProps) => {
  // If invalid file type show error toast
  if (!regex.test(file.name.toLowerCase())) {
    const fileExtension = file.name.split('.').pop()
    const errorResp = {
      message: `${fileExtension} file extension not supported.`,
    }
    errorResponseNotification(errorResp)
    return Promise.reject(errorResp)
  }
  // If there is an active upload with the same name show error toast
  if (
    uploads.find(
      upload =>
        upload.fileName === file.name &&
        upload.status === EventConstants.FILE_STATUS_ACTIVE,
    )
  ) {
    const errorResp = {
      message: 'Found an active upload with the same file name.',
    }
    errorResponseNotification(errorResp)
    return Promise.reject(errorResp)
  }

  // To be able to cancel the upload request need a cancel token
  const cancelSource = axios.CancelToken.source()

  const fileBeingUploaded: FileInfo = {
    fileName: file.name,
    fileSize: bytesToSize(file.size),
    progress: 5, // initialise with 5 to show some upload activity started
    progressMessage: 'Sending file...',
    status: EventConstants.FILE_STATUS_ACTIVE,
    isCancellable: true,
    cancel: function () {
      cancelSource.cancel()
      this.progressMessage = 'Cancelled'
    },
  }
  statusCallback({ ...fileBeingUploaded }, 'ADD') // Add the file being uploaded to the uploads list
  const url = `${EventConstants.EVENT_FILES}?postFeedback=${
    postFeedback ?? true
  }`

  var fd = new FormData()
  fd.append('file', file)
  fd.append('eventObjectId', eventObjectId)
  if (uploaderObjectId) {
    fd.append('vendorObjectId', uploaderObjectId)
  }
  if (eventName) {
    fd.append('eventName', eventName)
  }
  if (bpId) {
    fd.append('bpId', bpId.toString())
  }
  if (bpName) {
    fd.append('bpName', bpName)
  }

  return AxiosUtil.put<FileMetadata>(url, fd, {
    onUploadProgress: progressEvent => {
      if (progressEvent.lengthComputable) {
        var newProgress =
          0.95 * (progressEvent.loaded / progressEvent.total) * 100 // give 80% weightage to data upload. Remaining 20% when the http call is completed.
        if (fileBeingUploaded.progress + 5 < newProgress) {
          //update for every 5% incremenet
          const file = { ...fileBeingUploaded }
          file.progress = newProgress
          statusCallback(file, 'UPDATE')
        }
        if (newProgress > 85) {
          fileBeingUploaded.isCancellable = false
          fileBeingUploaded.progressMessage = 'Saving your file..'
        }
      }
    },
    cancelToken: cancelSource.token, // Allows request to be cancelled
  })
    .then(response => {
      return onFileUploadComplete(
        { ...fileBeingUploaded, progressMessage: 'Saving your file...' },
        statusCallback,
        response,
      )
    })
    .catch(err => {
      onFileUploadError(fileBeingUploaded, statusCallback, err)
      throw err
    })
}

const onFileUploadError = (fileBeingUploaded, statusCallback, error) => {
  const file = { ...fileBeingUploaded }
  file.progress = 100
  file.isCancellable = false
  file.status = EventConstants.FILE_STATUS_FAILED
  if (file.progressMessage !== 'Cancelled') {
    file.progressMessage = 'Failed'
  }
  statusCallback(file, 'UPDATE')
  errorResponseNotification(error)
  throw error
}

const onFileUploadComplete = (
  fileBeingUploaded: FileInfo,
  statusCallback: (file: FileInfo, status: string) => void,
  response: AxiosResponse<FileMetadata>,
) => {
  const file = { ...fileBeingUploaded }
  file.progress = 100
  file.isCancellable = false
  if (response == null) {
    file.status = EventConstants.FILE_STATUS_FAILED
    file.progressMessage = 'Failed'
  } else {
    file.status = EventConstants.FILE_STATUS_SUCCESS
    file.progressMessage = 'Done'
  }
  statusCallback(file, 'UPDATE')
  return response?.data
}

export const deleteFiles = (
  eventObjId,
  vendorObjId,
  files,
  eventName,
  bpId,
  bpName,
) => {
  // Get all of the file keys
  let fileKeys
  try {
    fileKeys = files.map(file => {
      // External users cannot delete files shared to them by Target
      if (vendorObjId && file.fileOwner === EventConstants.TARGET) {
        var errorResp = {
          message: 'Unselect TARGET uploaded files and try again.',
        }
        if (files.length === 1) {
          errorResp.message = 'Cannot delete TARGET uploaded files.'
        }
        errorResponseNotification(errorResp)
        throw new Error(errorResp.message)
      } else {
        return file.fileKey
      }
    })
  } catch (err) {
    return Promise.reject(err).catch(err => {
      errorResponseNotification(err)
      throw err
    })
  }

  let url =
    EventConstants.EVENT_FILES +
    '?eventObjectId=' +
    eventObjId +
    '&eventName=' +
    eventName +
    '&bpId=' +
    bpId +
    '&bpName=' +
    bpName +
    '&fileKeys=' +
    fileKeys
  if (vendorObjId) {
    url += '&vendorObjectId=' + vendorObjId
  }

  return AxiosUtil.delete(url).catch(err => {
    errorResponseNotification(err)
    throw err
  })
}

// Download file for external users
export const downloadFile = (file: FileDownload, vendorObjId: string) => {
  const url =
    EventConstants.EVENT_FILES_DOWNLOADS +
    '?fileKey=' +
    file.fileKey +
    '&fileName=' +
    file.fileName +
    '&vendorObjectId=' +
    vendorObjId
  return AxiosUtil.get(url, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    responseType: 'blob',
  })
    .then(res => {
      const blob = new Blob([res?.data], {
        type: 'application/octet-stream',
      })
      saveAs(blob, decodeURIComponent(file.fileName))
    })
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

// Download file for internal users
export const internalDownloadFile = (
  files: FileDownload[],
  onDownloadsStarted?: () => void,
) => {
  const url = EventConstants.EVENT_FILES_URLS
  return AxiosUtil.post(url, files)
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
    .then(res => {
      const fileUrls = res?.data
      // Set an interval to give enough time for the url links to be clicked in 'clickUrl' function below
      const interval = setInterval(
        urls => {
          const url = urls.pop()
          const link = document.createElement('a')
          link.setAttribute('href', url)
          link.click()
          link.remove()

          // Once all of the urls have been 'clicked', clear the interval and reset state
          if (urls.length === 0) {
            clearInterval(interval)
            onDownloadsStarted?.()
          }
        },
        1000,
        fileUrls,
      )
    })
}

export const shareFile = (fileShareChanges: ShareFileRequest) => {
  const url = EventConstants.EVENT_FILES_SHARES
  return AxiosUtil.post(url, fileShareChanges)
    .then(res => successNotification('Successfully updated the file sharing.'))
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

type UploadAndShareProps = { userType: string } & UploadFileProps &
  Pick<ShareFileRequest, 'eventName' | 'vendorsToAdd' | 'bpsInfo'>

export const uploadAndShare = async ({
  userType,
  eventObjectId,
  uploaderObjectId,
  statusCallback,
  file,
  uploads,
  eventName,
  bpId,
  bpName,
  vendorsToAdd,
  bpsInfo,
}: UploadAndShareProps): Promise<string> => {
  // upload
  const response = await uploadFile({
    eventObjectId,
    uploaderObjectId,
    statusCallback,
    file,
    uploads,
    eventName,
    bpId,
    bpName,
    postFeedback: false,
  })

  const { fileKey } = response

  if (userType === EventConstants.BUYER) {
    // take file info and share, not needed for external users
    await shareFile({
      fileKey,
      eventId: eventObjectId,
      eventName,
      vendorsToAdd,
      vendorsToRemove: [],
      bpsInfo,
      postFeedback: false,
    })
  }

  return fileKey
}

export const shortenFileName = (fileName: string) => {
  if (fileName.length < 40) {
    return fileName
  }
  const fileExtension = fileName.split('.').pop()
  return `${fileName.substring(0, 30)}...  .${fileExtension}`
}
